import {
    DndContext,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    rectIntersection,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { useCreateEvent, useDeleteEvent, useUpdateEvent } from '@hooks'
import { getErrorMessage } from '@lib/errorUtils'
import { EventCategory, PutEventType } from '@orbit/api-types'
import { Event } from '@types'
import React from 'react'
import toast from 'react-hot-toast'
import Column from '../Column/Column'
import DateSelector from '../DateSelector/DateSelector'
import {
    BoardContainer,
    ColumnsContainer,
    EmptyState,
    EmptyStateDescription,
    EmptyStateIcon,
    EmptyStateTitle,
    ScrollableColumnsWrapper,
} from './Board.styles'
import { BoardProps } from './types'

const Board: React.FC<BoardProps> = ({ itinerary, tripId, tripStartDate, tripEndDate, onEditDatesClick }) => {
	const [activeEvent, setActiveEvent] = React.useState<Event | null>(null)
	const createEventMutation = useCreateEvent()
	const updateEventMutation = useUpdateEvent()
	const deleteEventMutation = useDeleteEvent()

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
	)

	const handleDragStart = (dragStartEvent: DragStartEvent) => {
		const activeId = dragStartEvent.active.id as string

		// Extract event ID from prefixed active ID
		const eventId = activeId.replace('event-', '')

		// Find the active event for the drag overlay
		const sourceDay = itinerary.days.find(day =>
			day.events.some(event => event.id.toString() === eventId),
		)
		const event = sourceDay?.events.find(event => event.id.toString() === eventId)
		setActiveEvent(event || null)
	}

	const handleDragOver = () => {
		// This is handled by the Column component's useDndMonitor
		// No action needed here, but the event must be present for useDndMonitor to work
	}

	const handleDragEnd = async () => {
		// This is handled by the Column component's useDndMonitor
		// No action needed here, but the event must be present for useDndMonitor to work
	}

	const handleAddEvent = async (
		dayId: string,
		title: string,
		description: string,
		category?: EventCategory,
		location?: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		},
		links?: string[],
		startTime?: string | null,
		createdFromIdeaId?: number,
	) => {
		const day = itinerary.days.find(d => d.id === dayId)
		if (!day) return

		const tripDayIndex = Number(dayId)

		// Filter out empty links
		const filteredLinks = links?.filter(link => link.trim() !== '') || []

		const eventData = {
			name: title,
			description: description || undefined,
			category: category || ('Other' as EventCategory),
			location: location || undefined,
			tripDayIndex,
			tripEventLinks: filteredLinks,
			tags: [],
			...(startTime && { startTime }),
			...(createdFromIdeaId && { createdFromIdeaId }),
			isDeleted: false,
			deletedAt: null,
		}

		try {
			await createEventMutation.mutateAsync({
				tripId,
				eventData,
			})
			toast.success(`'${eventData.name}' added successfully!`)
		} catch (error) {
			console.error('Failed to create event:', error)
			const errorMessage = getErrorMessage(error, 'Failed to add event. Please try again.')
			toast.error(errorMessage)
		}
	}

	const handleUpdateEvent = async (eventId: string, updates: PutEventType) => {
		// Find the event to get its current data
		const event = itinerary.days.flatMap(day => day.events).find(e => e.id.toString() === eventId)

		if (!event) return

		try {
			await updateEventMutation.mutateAsync({
				tripId,
				eventId,
				eventData: updates,
			})
			const eventName = updates.name ?? event.name
			toast.success(`'${eventName}' updated successfully!`)
		} catch (error) {
			console.error('Failed to update event:', error)
			const errorMessage = getErrorMessage(error, 'Failed to update event. Please try again.')
			toast.error(errorMessage)
		}
	}

	const handleDeleteEvent = async (eventId: string) => {
		// Find the event to get its name before deleting
		const event = itinerary.days.flatMap(day => day.events).find(e => e.id.toString() === eventId)
		const eventName = event?.name || 'Event'

		try {
			await deleteEventMutation.mutateAsync({
				tripId,
				eventId,
			})
			toast.success(`'${eventName}' deleted successfully!`)
		} catch (error) {
			console.error('Failed to delete event:', error)
			const errorMessage = getErrorMessage(error, 'Failed to delete event. Please try again.')
			toast.error(errorMessage)
		}
	}

	if (!itinerary.days || itinerary.days.length === 0) {
		return (
			<BoardContainer>
				<EmptyState>
					<EmptyStateIcon>📅</EmptyStateIcon>
					<EmptyStateTitle>No Itinerary Yet</EmptyStateTitle>
					<EmptyStateDescription>
						Select your travel dates to generate your itinerary board
					</EmptyStateDescription>
				</EmptyState>
			</BoardContainer>
		)
	}

	return (
		<BoardContainer>
			<DateSelector
				initialStartDate={tripStartDate}
				initialEndDate={tripEndDate}
				onEditClick={onEditDatesClick}
			/>

			<DndContext
				sensors={sensors}
				collisionDetection={rectIntersection}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
			>
				<ScrollableColumnsWrapper>
					<ColumnsContainer id="board-column-container">
						{itinerary.days.map(day => (
							<Column
								key={day.id}
								day={day}
								tripId={tripId}
								onAddEvent={handleAddEvent}
								onUpdateEvent={handleUpdateEvent}
								onDeleteEvent={handleDeleteEvent}
							/>
						))}
					</ColumnsContainer>
				</ScrollableColumnsWrapper>
				<DragOverlay>
					{activeEvent ? (
						<div
							style={{
								transform: 'rotate(5deg)',
								opacity: 0.9,
								boxShadow: '0 8px 25px rgba(0, 0, 0, 0.25)',
								background: '#ffffff',
								border: '1px solid #e1e5e9',
								borderRadius: '8px',
								padding: '1rem',
								minWidth: '200px',
								maxWidth: '280px',
							}}
						>
							<div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
								{activeEvent.name}
							</div>
							{activeEvent.description && (
								<div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
									{activeEvent.description}
								</div>
							)}
						</div>
					) : null}
				</DragOverlay>
			</DndContext>
		</BoardContainer>
	)
}

export default Board
